import type { JSX } from "solid-js"

/**
 * 定时任务页自用图标集。
 * 统一 16×16 viewBox、1.5px 描边，避免依赖 v2 Icon 里并不存在的时钟/日历类图标。
 */
const PATHS: Record<string, () => JSX.Element> = {
  clock: () => (
    <>
      <circle cx="8" cy="8" r="5.75" />
      <path d="M8 4.9V8.25l2.25 1.35" />
    </>
  ),
  history: () => (
    <>
      <path d="M1.9 8a6.1 6.1 0 1 0 1.8-4.3" />
      <path d="M1.6 2.2v2.6h2.6" />
      <path d="M8 4.9V8.25l2.25 1.35" />
    </>
  ),
  calendar: () => (
    <>
      <rect x="2.1" y="3.2" width="11.8" height="10.7" rx="1.8" />
      <path d="M2.1 6.4h11.8M5.4 1.9v2.4M10.6 1.9v2.4" />
    </>
  ),
  week: () => (
    <>
      <rect x="2.1" y="3.2" width="11.8" height="10.7" rx="1.8" />
      <path d="M2.1 6.4h11.8M5.4 1.9v2.4M10.6 1.9v2.4" />
      <path d="M5.2 8.7h2M5.2 11.1h4.2" />
    </>
  ),
  month: () => (
    <>
      <rect x="2.1" y="3.2" width="11.8" height="10.7" rx="1.8" />
      <path d="M2.1 6.4h11.8M5.4 1.9v2.4M10.6 1.9v2.4" />
      <circle cx="5.5" cy="9.1" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="8" cy="9.1" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="9.1" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="11.6" r="0.85" fill="currentColor" stroke="none" />
      <circle cx="8" cy="11.6" r="0.85" fill="currentColor" stroke="none" />
    </>
  ),
  bolt: () => <path d="M8.9 1.9 3.6 9h3.6l-.9 5.1L11.9 7H8.3l.6-5.1Z" />,
  play: () => <path d="M5.4 3.6 12 8l-6.6 4.4V3.6Z" />,
  pause: () => <path d="M6 3.9v8.2M10 3.9v8.2" />,
  edit: () => (
    <>
      <path d="M11.6 2.4a1.7 1.7 0 0 1 2.4 2.4l-7.3 7.3-3 .7.7-3 7.2-7.4Z" />
      <path d="M2.6 13.4h10.8" />
    </>
  ),
  trash: () => (
    <>
      <path d="M2.6 4.4h10.8M6.4 4.4V2.7h3.2v1.7M4 4.4l.7 9h6.6l.7-9" />
      <path d="M6.6 6.8v4.4M9.4 6.8v4.4" />
    </>
  ),
  search: () => (
    <>
      <circle cx="7.1" cy="7.1" r="4.4" />
      <path d="m10.5 10.5 3 3" />
    </>
  ),
  close: () => <path d="m4 4 8 8M12 4l-8 8" />,
  plus: () => <path d="M8 3.2v9.6M3.2 8h9.6" />,
  folder: () => (
    <path d="M1.9 12.6V3.4h4.2l1.6 1.9h6.4v7.3a.9.9 0 0 1-.9.9H2.8a.9.9 0 0 1-.9-.9Z" />
  ),
  check: () => <path d="m3.2 8.4 3.2 3.2 6.4-7" />,
  alert: () => (
    <>
      <path d="M8 2.2 14.4 13H1.6L8 2.2Z" />
      <path d="M8 6.3v3.2M8 11.3v.05" />
    </>
  ),
  terminal: () => (
    <>
      <rect x="1.9" y="2.9" width="12.2" height="10.2" rx="1.7" />
      <path d="m4.6 6.4 1.9 1.9-1.9 1.9M8.6 10.4h2.8" />
    </>
  ),
  chevron: () => <path d="m5.6 6.4 2.4 2.6 2.4-2.6" />,
  spark: () => (
    <>
      <path d="M8 1.8v3.1M8 11.1v3.1M1.8 8h3.1M11.1 8h3.1" />
      <path d="m3.7 3.7 1.9 1.9M10.4 10.4l1.9 1.9M12.3 3.7l-1.9 1.9M5.6 10.4l-1.9 1.9" />
    </>
  ),
}

export type StIconName = keyof typeof PATHS

export function StIcon(props: { name: StIconName; size?: number; class?: string }) {
  return (
    <svg
      class={props.class}
      width={props.size ?? 16}
      height={props.size ?? 16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.4"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {PATHS[props.name]?.()}
    </svg>
  )
}
